import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { UseDataProps } from './types';
import useEffectOnce from '80.quickConnect.Core/hooks/useEffectOnce';
import RootStore from '30.quickConnect.Stores/RootStore';
import { AppModulesIDs } from '30.quickConnect.Stores/RootStore/LoginStore/Payloads/responses';
import {
  WORK_SITE_SELECT,
  DECLARATIONS,
  EQUIPMENTS,
  FORMS,
  HOME,
  INBOXES,
  QRSCAN,
} from '10.quickConnect.app/routes/paths';
import { MAIN_PAGE_SUBROUTES } from '10.quickConnect.app/routes/routes';
import { HomeTabs } from '90.quickConnect.Models/enums';
import { useStore } from '30.quickConnect.Stores';
import { useNavigatorOnLine } from '80.quickConnect.Core/hooks';
import { useModuleContext, useModuleContextSetter } from '00.app/module/moduleContext';

const useData = (): UseDataProps => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // On récupère le store
  const {
    InboxesStore: { getInboxItems, inboxesTotalNumber },
    EquipmentsStore: { getEquipmentsFromDb },
    DeclarationStore: { sendDeclarationToAPIAsync, sendAPI, backupDclContext, getDeclarationList },
    LoginStore: {
      signInInfos: { userUPN, module },
      getAvailableModules,
    },
    UserSettingsStore: { syncDate },
    SessionStore: { lastURLSaved },
    CommonStore: { setWorkInProgress, setWorkDone },
    sendLocalDataBaseRecordsCount,
  } = useStore();

  const { t } = useTranslation();
  const { t: tDeclaration } = useTranslation('declaration');
  const { t: tAxios } = useTranslation('axios');
  const onLine = useNavigatorOnLine();
  const contextModule = useModuleContext();
  const setModule = useModuleContextSetter();
  const location = useLocation();

  const sendAPIRef = useRef<boolean>(true);

  useLayoutEffect(() => {
    if (sendAPIRef.current === true && onLine) {
      (async () => {
        await sendLocalDataBaseRecordsCount(syncDate);
        await sendDeclarationToAPIAsync(tDeclaration, tAxios);
      })();

      return () => {
        if (onLine) sendAPIRef.current = false;
      };
    }

    if (!onLine) {
      sendAPIRef.current = true;
    }
  }, [
    onLine,
    sendLocalDataBaseRecordsCount,
    syncDate,
    sendDeclarationToAPIAsync,
    userUPN,
    setWorkInProgress,
    setWorkDone,
    tDeclaration,
    tAxios,
  ]);

  // On set le state
  const [valueTabIndex, setValueTabIndex] = useState<HomeTabs>(() => {
    switch (pathname.toLowerCase()) {
      case EQUIPMENTS:
        return HomeTabs.Equipments;
      case INBOXES:
        return HomeTabs.Inboxes;
      case DECLARATIONS:
        return HomeTabs.Declarations;
      case QRSCAN:
        return HomeTabs.QrScan;
      case HOME:
      case FORMS:
      default:
        return HomeTabs.Forms;
    }
  });
  const valueTabIndexs = useMemo(() => {
    switch (pathname.toLowerCase()) {
      case EQUIPMENTS:
        return HomeTabs.Equipments;
      case INBOXES:
        return HomeTabs.Inboxes;
      case DECLARATIONS:
        return HomeTabs.Declarations;
      case QRSCAN:
        return HomeTabs.QrScan;
      case HOME:
      case FORMS:
      default:
        return HomeTabs.Forms;
    }
  }, [pathname]);

  // On first load/login we synchronise the context with the Signin info. From then, we should use the context value
  useEffectOnce(() => {
    if (module !== contextModule) {
      setModule(module);
    }
  });

  useEffect(() => {
    setValueTabIndex(valueTabIndexs);
  }, [valueTabIndexs]);

  // Force the module to be DEFAULT after log in when on tne FORMS page
  useEffect(() => {
    if (module !== AppModulesIDs.MODULE_WEB_CLIENT_ID && location.pathname === FORMS) {
      setModule(AppModulesIDs.MODULE_WEB_CLIENT_ID);
    }
  }, [location, module, setModule]);

  // On ajoute des hooks
  useEffect(() => {
    const getUrlByModule = () => {
      switch (module) {
        case AppModulesIDs.MODULE_PARAM_WORK_SITE_ID:
          return WORK_SITE_SELECT;
        default:
          return FORMS;
      }
    };

    // Par défault, on envoie vers la liste des formulaires
    const navUrl = lastURLSaved && backupDclContext.length === 0 ? lastURLSaved : getUrlByModule();
    if (pathname === HOME) navigate(navUrl, { replace: true });
  }, [navigate, pathname, lastURLSaved, backupDclContext, module]);

  // On ajoute des hooks
  const handleTabChange = useCallback(
    (newTab: HomeTabs) => {
      setValueTabIndex(newTab);
      const target = MAIN_PAGE_SUBROUTES.find((tab) => tab.homeTab === newTab);
      if (target) navigate(target.path ?? '/');
      else navigate(FORMS, { replace: true });
    },
    [navigate],
  );

  const getInboxesJob = useMemo(() => [uuidv4(), t('inboxes:jobMessage.getInboxesJob')] as [string, string], [t]);

  const shouldGetInboxesList = useRef(true);

  useEffect(() => {
    if (shouldGetInboxesList.current && !lastURLSaved) {
      shouldGetInboxesList.current = false;
      setWorkInProgress(getInboxesJob);
      getInboxItems().finally(() => {
        setWorkDone(getInboxesJob[0]);
      });
    }
  }, [getInboxItems, getInboxesJob, setWorkDone, setWorkInProgress, lastURLSaved]);

  const getDeclarationsJob = useMemo(
    () => [uuidv4(), t('declarations:job_message_get_declarations_job')] as [string, string],
    [t],
  );

  const shouldGetDeclarationsList = useRef(true);

  useEffect(() => {
    if (shouldGetDeclarationsList.current && !lastURLSaved) {
      shouldGetDeclarationsList.current = false;
      setWorkInProgress(getDeclarationsJob);
      getDeclarationList(userUPN).finally(() => {
        setWorkDone(getDeclarationsJob[0]);
      });
    }
  }, [getDeclarationList, getDeclarationsJob, setWorkDone, setWorkInProgress, lastURLSaved, userUPN]);

  const getEquipmentsJob = useMemo(
    () => [uuidv4(), t('equipment:jobMessage.getEquipmentsJob')] as [string, string],
    [t],
  );

  const shouldGetEquipmentsList = useRef(true);

  useEffect(() => {
    if (shouldGetEquipmentsList.current && !lastURLSaved) {
      shouldGetEquipmentsList.current = false;
      setWorkInProgress(getEquipmentsJob);
      getEquipmentsFromDb().finally(() => {
        setWorkDone(getEquipmentsJob[0]);
      });
    }
  }, [getEquipmentsFromDb, getEquipmentsJob, setWorkDone, setWorkInProgress, lastURLSaved]);

  const getAvailableModuleJob = useMemo(
    () => [uuidv4(), t('declarations:job_message_get_declarations_job')] as [string, string],
    [t],
  );
  const shouldGetAvailableModules = useRef(true);

  useEffect(() => {
    if (shouldGetAvailableModules.current && !lastURLSaved) {
      shouldGetAvailableModules.current = false;
      setWorkInProgress(getAvailableModuleJob);
      RootStore.getInstance().LoginStore.updateAvailableModules();
      setWorkDone(getAvailableModuleJob[0]);
    }
  }, [getAvailableModuleJob, getAvailableModules, lastURLSaved, setWorkDone, setWorkInProgress]);

  return {
    handleTabChange,
    valueTabIndex,
    inboxesTotalNumber,
    sendAPI,
  };
};

export default useData;
